import React, { useState } from 'react';
import { useLayerControl } from './hooks/useLayerControl';
import './styles/LayerControl.css';

const LayerControl = ({ map, mapReady, currentMode }) => {
  // Use layer control hook
  const { 
    layerVisibility, 
    toggleLayer,
    distanceLabelsAutoHide
  } = useLayerControl(map, mapReady);

  // Whether layer control panel is expanded
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine if layer buttons should be disabled based on current mode
  const isLayerDisabledForMode = (layerKey) => {
    switch (currentMode) {
      case 'Setup Points':
        // Setup mode: only allow location points control
        return layerKey !== 'locationPoints';
      
      case 'Custom Path':
        // Custom mode: auto path button not operable
        return layerKey === 'autoPath';
      
      case 'Auto Path':
        // Auto mode: custom path button not operable
        return layerKey === 'customPath';
      
      case 'Compare Paths':
        // Compare mode: all layers operable
        return false;
      
      default:
        return false;
    }
  };

  // Layer configuration
  const layers = [
    {
      key: 'autoPath',
      label: 'Auto Path',
      description: 'Algorithm-generated optimal path',
      isModeDisabled: isLayerDisabledForMode('autoPath')
    },
    {
      key: 'customPath',
      label: 'Custom Path',
      description: 'User manually edited path',
      isModeDisabled: isLayerDisabledForMode('customPath')
    },
    {
      key: 'locationPoints',
      label: 'Location Points',
      description: 'Location markers and numbers',
      isModeDisabled: isLayerDisabledForMode('locationPoints')
    },
    {
      key: 'distanceLabels',
      label: 'Distance Labels',
      description: distanceLabelsAutoHide?.shouldAutoHide 
        ? `Path segment distance info (force hidden at city level)`
        : 'Path segment distance information',
      isAutoHidden: distanceLabelsAutoHide?.isAutoHidden,
      isDisabled: distanceLabelsAutoHide?.shouldAutoHide && !layerVisibility.distanceLabels,
      isModeDisabled: isLayerDisabledForMode('distanceLabels')
    }
  ];

  return (
    <div className={`layer-control ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Control panel header */}
      <div 
        className="layer-control-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="header-content">
          <span className="header-title">Layer Control</span>
        </div>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          ▼
        </span>
      </div>

      {/* Layer control list */}
      {isExpanded && (
        <div className="layer-control-body">
          {layers.map(layer => {
            // Combine all disable conditions
            const totalDisabled = layer.isDisabled || layer.isModeDisabled;
            
            return (
              <div key={layer.key} className={`layer-item ${layer.isAutoHidden ? 'auto-hidden' : ''} ${totalDisabled ? 'disabled' : ''} ${layer.isModeDisabled ? 'mode-disabled' : ''}`}>
                <div className="layer-info">
                  <div className="layer-header">
                    <span className="layer-label">
                      {layer.label}
                      {layer.isAutoHidden && (
                        <span className="auto-hide-indicator" title="Currently force hidden due to zoom level">
                          🚫
                        </span>
                      )}
                      {layer.isModeDisabled && (
                        <span className="mode-disabled-indicator" title={`Not operable in current mode (${currentMode})`}>
                          🔒
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="layer-description">
                    {layer.description}
                    {layer.isModeDisabled && (
                      <span className="mode-disabled-hint"> (unavailable in current mode)</span>
                    )}
                  </div>
                </div>
                
                <label className={`layer-toggle ${totalDisabled ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={layerVisibility[layer.key]}
                    onChange={() => {
                      // Check if disabled due to mode
                      if (layer.isModeDisabled) {
                        return false;
                      }
                      return toggleLayer(layer.key);
                    }}
                    disabled={totalDisabled}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LayerControl;
